import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CartService, LineItem} from '../cart.service';
import {catchError, debounce, filter, first, switchMap} from 'rxjs/operators';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {interval, Observable, of, Subscription} from 'rxjs';
import {Cart, CartItem} from '../Cart';
import {Graphic, GraphicService} from '../../graphic/graphic.service';
import {AddOffer, AddProduct, ContractTerm, Messages, ProductService, UpgradeProtectionType} from '@uofu-uss-aux/software-catalog-ng-core';
import {NotificationService} from '@uofu-uss/angular-util';
import {CurrencyPipe, DatePipe, NgForOf, NgIf, TitleCasePipe} from '@angular/common';
import {UnderscoreToSpacePipe} from '../../formatting/underscore-to-space.pipe';
import {LicenseExpirationWarningComponent} from '../../product/license-expiration-warning/license-expiration-warning.component';
import {CatalogGraphicComponent} from '../../graphic/catalog-graphic/catalog-graphic.component';

export enum CartItemProcessingStatus {
  Processing,
  Complete,
}

@Component({
  selector: 'app-cart-item',
  templateUrl: './cart-item.component.html',
  styleUrls: ['./cart-item.component.scss'],
  standalone: true,

  imports: [
    NgIf,
    TitleCasePipe,
    UnderscoreToSpacePipe,
    DatePipe,
    ReactiveFormsModule,
    CurrencyPipe,
    NgForOf,
    LicenseExpirationWarningComponent,
    CatalogGraphicComponent,
  ]
})
export class CartItemComponent extends AddProduct(AddOffer(Object)) implements OnInit, OnDestroy {

  subscription: Subscription;
  quantityForm: UntypedFormGroup;
  graphic: Graphic;

  individualPrice = 0;
  processing = false;

  offerAsArray = [];

  @Input() editable = false;
  @Input() cart: Cart;
  @Output() processingStatusChanged = new EventEmitter<CartItemProcessingStatus>();
  @Output() itemUpdated = new EventEmitter<LineItem>();

  #lineItem: LineItem;

  constructor(private readonly cartService: CartService,
              private readonly notificationService: NotificationService,
              private readonly graphicService: GraphicService,
              private readonly productService: ProductService) {
    super();

    this.quantityForm = new UntypedFormGroup({
      quantity: new UntypedFormControl(0, [Validators.min(1), Validators.max(50000), Validators.required]),
    });
  }

  ngOnInit(): void {
    this.subscription = this.quantityForm.controls.quantity.valueChanges
      .pipe(
        debounce(() => interval(600)),
        filter(() => !!this.item),
        filter(() => this.quantityForm.controls.quantity.valid),
        filter(() => this.item.quantity !== this.quantityForm.controls.quantity.value),
        switchMap(qty => this.updateCartItem(qty).pipe(catchError(this.updateCartError))),
        filter(cart => !!cart))
      .subscribe((cart) => this.updateQuantitySuccess(this.item.id, cart));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get item(): CartItem {
    return this.lineItem?.cartItem;
  }

  get lineItem(): LineItem {
    return this.#lineItem;
  }

  @Input()
  set lineItem(given) {
    this.clearItem();

    this.#lineItem = given;
    if (given) {
      this.individualPrice = this.item.price / this.item.quantity;
      this.product = this.lineItem.product;
      this.offer = this.lineItem.offering;
      this.offerAsArray = [this.offer];

      this.quantityForm.controls.quantity.setValue(this.item.quantity);

      this.loadGraphic(this.imageId);
    }
  }

  private clearItem(): void {
    this.individualPrice = 0;
    this.product = null;
    this.offer = null;
    this.offerAsArray = [];
    this.graphic = null;
  }

  private loadGraphic(id: number): void {
    if (id) {
      this.graphicService.fetchGraphic(id)
        .pipe(first())
        .subscribe(next => this.graphic = next);
    }
  }

  private readonly updateCartItem = (quantity: number): Observable<Cart> => {
    this.initiateProcessing();
    return this.cartService.updateCartItemQty(this.item, quantity, this.cart);
  }

  private initiateProcessing(): void {
    console.trace(`initiating processing with current value of ${this.processing}`);
    if (this.processing) {
      this.processingStatusChanged.emit(CartItemProcessingStatus.Complete);
    }
    this.processingStatusChanged.emit(CartItemProcessingStatus.Processing);
    this.processing = true;
  }

  private completeProcessing(): void {
    console.trace(`completing processing with current value of ${this.processing}`);
    this.processingStatusChanged.emit(CartItemProcessingStatus.Complete);
    this.processing = false;
  }

  remove(): void {
    if (this.item?.id) {
      console.debug(`Attempting to remove item: ${this.item.id}`);
      this.initiateProcessing();
      const observer = {
        next: this.removeItemSuccess,
        error: this.updateCartError,
      };
      this.cartService.removeFromCart(this.item.id, this.cart).subscribe(observer);
    }
  }

  readonly updateCartError = (errorResponse): Observable<Cart> => {
    console.error(`Failed to update item. resetting quantity: ${errorResponse.status}`);
    this.completeProcessing();

    const body = Messages.validationMsg(errorResponse, 'Unable to update cart');
    this.notificationService.addError({
      title: 'Error',
      body,
    });
    // restore the item.
    this.quantityForm.controls.quantity.setValue(this.item?.quantity);

    return of(null);
  }

  readonly updateQuantitySuccess = (itemId: number, cart: Cart) => {
    console.trace(`Cart item ${itemId} updated`);
    this.completeProcessing();
    const updated = cart.items.find(item => item.id === itemId);
    if (updated) {
      this.lineItem = {
        cartItem: updated,
        cart,
        offering: this.offer,
        product: this.product,
      };
    }
    this.itemUpdated.emit(this.lineItem);
    this.cartService.value = cart;
  }

  readonly removeItemSuccess = (cart: Cart) => {
    this.completeProcessing();
    this.lineItem = null;
    this.itemUpdated.emit(null);
    this.cartService.value = cart;
  }

  get serialNumbers() {
    return this.item?.serialNumbers;
  }

  get licenseType() {
    return this.productService.findLicenseFromOffering(this.product, this.offer).type;
  }

  get hasPerpetualLicense() {
    return this.contractTerm === ContractTerm.PerpetualLicense;
  }

  get hasUpgradeProtection() {
    return this.upgradeProtectionType === UpgradeProtectionType.Included;
  }

  get isUpgradeProtectionOnly() {
    return this.upgradeProtectionType === UpgradeProtectionType.UpgradeProtectionOnly;
  }

  get hasEditableOffering() {
    return this.editable && !!this.offer;
  }
}
